/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/phone/QaPhoneHeader.tsx
 * FILE_ID: JOBHUB-QA-PHONE-HEADER-001
 * PURPOSE: Area + checklist tap targets, PDF, progress. Full height so the
 *          solid background covers both lines. The workspace wrapper holds it
 *          above the scroller; neither is sticky.
 * LANDMINE: the area and checklist lines are SEPARATE buttons (mockup:
 *   tapping the area name changes area, tapping the checklist name changes
 *   checklist). They stack inside one 44px column — keep each full-width so
 *   the two taps cannot mis-fire on a phone.
 */
import { ChevronDown, FileDown, LayoutGrid, Table2 } from 'lucide-react'
import { QA_PHONE_CONTROLS } from './qaPhoneControlInventory'
import type { QaBuildView } from './useQaPhoneUi'

export function QaPhoneHeader({
  areaName,
  checklistName,
  checklistCount = null,
  done,
  total,
  percent,
  onOpenScope,
  onOpenChecklist,
  onOpenItp,
  buildView = 'cards',
  onToggleBuildView,
}: {
  areaName: string
  checklistName: string | null
  /** "done/total" for the one checklist in view; the body no longer repeats it. */
  checklistCount?: string | null
  done: number
  total: number
  percent: number
  onOpenScope: () => void
  /** Opens the checklist picker sheet. Absent -> checklist line falls back to the area sheet. */
  onOpenChecklist?: () => void
  /** Opens Export. Icon and position stay; only the label changed. */
  onOpenItp?: () => void
  buildView?: QaBuildView
  /** Absent outside Build mode, which is where the toggle has no meaning. */
  onToggleBuildView?: () => void
}) {
  return (
    <header className="flex shrink-0 flex-none flex-col bg-card px-2">
      <div className="flex min-w-0 items-center gap-2">
        <div className="flex min-h-[44px] min-w-0 flex-1 flex-col justify-center">
          <button
            type="button"
            onClick={onOpenScope}
            data-qa-phone-control={QA_PHONE_CONTROLS.area}
            className="flex min-h-[22px] w-full min-w-0 items-center gap-1 py-0.5 text-left text-sm font-semibold leading-tight text-foreground"
          >
            <span className="min-w-0 truncate">{areaName}</span>
            <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
          </button>
          {/* Always rendered: with no active checklist the line used to vanish,
              which left the phone with no way off the first checklist. */}
          <button
            type="button"
            onClick={onOpenChecklist ?? onOpenScope}
            data-qa-phone-control={QA_PHONE_CONTROLS.checklist}
            className="flex min-h-[22px] w-full min-w-0 items-center gap-1 py-0.5 text-left text-[11px] leading-tight text-muted-foreground"
          >
            <span className="min-w-0 truncate">{checklistName ?? 'Checklists…'}</span>
            {checklistCount ? <span className="shrink-0 tabular-nums">{checklistCount}</span> : null}
            <ChevronDown className="h-3 w-3 shrink-0" aria-hidden />
          </button>
        </div>
        {/* Build only. The icon shows the view you would SWITCH TO, which is
            how the same button serves both directions. */}
        {onToggleBuildView ? (
          <button
            type="button"
            onClick={onToggleBuildView}
            data-qa-phone-control={QA_PHONE_CONTROLS.buildViewToggle}
            aria-label={buildView === 'cards' ? 'Switch to table view' : 'Switch to card view'}
            title={buildView === 'cards' ? 'Table view' : 'Card view'}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:text-foreground"
          >
            {buildView === 'cards'
              ? <Table2 className="h-4 w-4" aria-hidden />
              : <LayoutGrid className="h-4 w-4" aria-hidden />}
          </button>
        ) : null}
        {onOpenItp ? (
          <button
            type="button"
            onClick={onOpenItp}
            aria-label="Export ITP"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:text-foreground"
          >
            <FileDown className="h-4 w-4" aria-hidden />
          </button>
        ) : null}
        <p
          data-qa-phone-control={QA_PHONE_CONTROLS.progress}
          className="shrink-0 text-[11px] tabular-nums text-muted-foreground"
        >
          {done}/{total} · {percent}%
        </p>
      </div>
      <div className="h-[3px] overflow-hidden rounded-full bg-secondary">
        <div
          className="h-full bg-primary"
          style={{ width: `${Math.min(100, percent)}%` }}
        />
      </div>
    </header>
  )
}
